import { createContext, useContext, useState, ReactNode } from 'react';
import Popup, { PopupButton } from './Popup';

export interface ShowPopupOptions {
  title?: string;
  message: ReactNode;
  type?: 'info' | 'success' | 'warning' | 'error' | 'confirm';
  buttons?: PopupButton[];
  closeOnOverlayClick?: boolean;
  onClose?: () => void;
}

interface PopupContextType {
  showPopup: (options: ShowPopupOptions) => void;
  hidePopup: () => void;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export function PopupProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ShowPopupOptions | null>(null);

  const showPopup = (newOptions: ShowPopupOptions) => {
    setOptions(newOptions);
    setIsOpen(true);
  };

  const hidePopup = () => {
    setIsOpen(false);
    if (options?.onClose) {
      options.onClose();
    }
  };

  // Wrap button click handlers to automatically close the popup by default
  const resolvedButtons = options?.buttons?.map((btn) => ({
    ...btn,
    onClick: () => {
      let shouldClose = true;
      if (btn.onClick) {
        const result = btn.onClick() as any;
        if (result === false) {
          shouldClose = false;
        }
      }
      if (shouldClose) {
        hidePopup();
      }
    }
  })) || [];

  return (
    <PopupContext.Provider value={{ showPopup, hidePopup }}>
      {children}
      {options && (
        <Popup
          isOpen={isOpen}
          onClose={hidePopup}
          title={options.title}
          message={options.message}
          type={options.type}
          buttons={resolvedButtons}
          closeOnOverlayClick={options.closeOnOverlayClick ?? true}
        />
      )}
    </PopupContext.Provider>
  );
}

export function usePopup() {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
}
